import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PostalArea {
  id: string;
  geo_type: string;
  code: string;
  name: string;
  slug: string | null;
  parent_code: string | null;
  parent_geo_type: string | null;
  path: string | null;
  population_total: number;
  population_data: Record<string, number>;
  income_data: Record<string, number>;
  properties_data: Record<string, number>;
  postcode_count: number;
  area: number;
  sync_run_id: string | null;
}

// Alla kolumner utom geometry. Gränserna är stora (en kommun kan vara över 200 kB) och används
// bara vid bygget av kartorna (scripts/build-postal-maps.ts), aldrig i webbläsaren.
// En enda sträng (inte sammanfogad), så att Supabase-klienten kan läsa ut typen.
const KOLUMNER =
  'id, geo_type, code, name, slug, parent_code, parent_geo_type, path, population_total, population_data, income_data, properties_data, postcode_count, area, sync_run_id';

export function useCounties() {
  return useQuery({
    queryKey: ['postal-areas', 'counties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'county')
        .order('name');
      if (error) throw error;
      return data as PostalArea[];
    },
  });
}

export function useCounty(code: string) {
  return useQuery({
    queryKey: ['postal-areas', 'county', code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'county')
        .eq('code', code)
        .maybeSingle();
      if (error) throw error;
      return data as PostalArea | null;
    },
    enabled: !!code,
  });
}

export function useMunicipalities(countyCode: string) {
  return useQuery({
    queryKey: ['postal-areas', 'municipalities', countyCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'municipality')
        .eq('parent_code', countyCode)
        .eq('parent_geo_type', 'county')
        .order('name');
      if (error) throw error;
      return data as PostalArea[];
    },
    enabled: !!countyCode,
  });
}

export function useMunicipality(code: string) {
  return useQuery({
    queryKey: ['postal-areas', 'municipality', code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'municipality')
        .eq('code', code)
        .maybeSingle();
      if (error) throw error;
      return data as PostalArea | null;
    },
    enabled: !!code,
  });
}

export function useLocalities(municipalityCode: string) {
  return useQuery({
    queryKey: ['postal-areas', 'localities', municipalityCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'locality')
        .eq('parent_code', municipalityCode)
        .eq('parent_geo_type', 'municipality')
        .order('name');
      if (error) throw error;
      return data as PostalArea[];
    },
    enabled: !!municipalityCode,
  });
}

export function useLocality(code: string) {
  return useQuery({
    queryKey: ['postal-areas', 'locality', code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'locality')
        .eq('code', code)
        .maybeSingle();
      if (error) throw error;
      return data as PostalArea | null;
    },
    enabled: !!code,
  });
}

// ── Slug-based lookups ───────────────────────────────────────
// SEO-friendly URLs use slugs (`stockholms-lan`, `eskilstuna`) instead of
// codes (`01`, `0484`). The migration in supabase/migrations/2026...
// _add_slug_to_postal_areas.sql backfills the `slug` column. Each level
// resolves slug → row directly via the unique index on
// (geo_type, parent_code, slug).

export function useCountyBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['postal-areas', 'county', 'by-slug', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'county')
        .eq('slug', slug!)
        .maybeSingle();
      if (error) throw error;
      return data as PostalArea | null;
    },
    enabled: !!slug,
  });
}

export function useMunicipalityBySlug(
  countyCode: string | undefined,
  slug: string | undefined,
) {
  return useQuery({
    queryKey: ['postal-areas', 'municipality', 'by-slug', countyCode, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'municipality')
        .eq('parent_code', countyCode!)
        .eq('parent_geo_type', 'county')
        .eq('slug', slug!)
        .maybeSingle();
      if (error) throw error;
      return data as PostalArea | null;
    },
    enabled: !!countyCode && !!slug,
  });
}

export function useLocalityBySlug(
  municipalityCode: string | undefined,
  slug: string | undefined,
) {
  return useQuery({
    queryKey: ['postal-areas', 'locality', 'by-slug', municipalityCode, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('postal_areas')
        .select(KOLUMNER)
        .eq('geo_type', 'locality')
        .eq('parent_code', municipalityCode!)
        .eq('parent_geo_type', 'municipality')
        .eq('slug', slug!)
        .maybeSingle();
      if (error) throw error;
      return data as PostalArea | null;
    },
    enabled: !!municipalityCode && !!slug,
  });
}
